import { create } from "zustand";
import { fetchPriceAlertsByPlatformIds, fetchPriceAlertsForAllRestaurants, PriceAlert } from "../services/priceAlerts.ts/priceAlertsService";
import { ALL_RESTAURANTS_ID } from "./appUserDefaultStore";

interface PriceAlertsState {
  priceAlerts: PriceAlert[];
  currentRestaurantId: string | null;
  isLoading: boolean;
  load: (restaurantId: string, platformIds: string[]) => Promise<void>;
  isDataLoadedFor: (restaurantId: string) => boolean;
}

export const usePriceAlertsStore = create<PriceAlertsState>((set, get) => ({
  priceAlerts: [],
  currentRestaurantId: null,
  isLoading: false,

  load: async (restaurantId: string, platformIds: string[]) => {
    // Skip if data is already loaded for this restaurant
    if (get().isDataLoadedFor(restaurantId)) {
      return;
    }

    set({ isLoading: true });

    try {
      let alerts: PriceAlert[] = [];

      if (restaurantId === ALL_RESTAURANTS_ID) {
        alerts = await fetchPriceAlertsForAllRestaurants();
      } else {
        if (platformIds.length === 0) {
          set({ priceAlerts: [], currentRestaurantId: restaurantId, isLoading: false });
          return;
        }
        alerts = await fetchPriceAlertsByPlatformIds(platformIds);
      }

      // Filter out alerts with >200% price change (likely data errors)
      const filteredAlerts = alerts.filter((alert) => {
        if (alert.fromPrice === 0) return false;
        const priceChangePercent = Math.abs((alert.toPrice - alert.fromPrice) / alert.fromPrice) * 100;
        return priceChangePercent <= 200;
      });

      // Sort chronologically by toDate (newest first)
      const sortedAlerts = filteredAlerts.sort((a, b) => new Date(b.toDate).getTime() - new Date(a.toDate).getTime());

      set({
        priceAlerts: sortedAlerts,
        currentRestaurantId: restaurantId,
        isLoading: false
      });
    } catch (err) {
      console.error('[ERROR] loading price alerts', err);
      set({ isLoading: false });
    }
  },

  isDataLoadedFor: (restaurantId: string) => get().currentRestaurantId === restaurantId
}));
